import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'

import type { TrendDetectionService } from 'features/trend-detection/service'
import type { IssueAgentResponse } from 'features/trend-detection/types'

export interface AgentRunInfo {
  issueCount: number
  lastRunTime: string | null
  runStatus: 'created' | 'running' | 'completed' | 'failed'
}

export const ISSUE_AGENTS_QUERY_KEY = 'issue-agents'

export interface UseAgentsListReturn {
  agents: IssueAgentResponse[]
  agentsPage: number
  setAgentsPage: (page: number) => void
  loadingAgents: boolean
  agentRunInfo: Record<string, AgentRunInfo | null>
  loadingRunInfo: boolean

  loadAgents: () => Promise<void>
}

export function useAgentsList(
  service: TrendDetectionService,
  projectId: string | null,
): UseAgentsListReturn {
  const [agentsPage, setAgentsPage] = useState<number>(1)

  const {
    data: summaryData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [ISSUE_AGENTS_QUERY_KEY, projectId],
    queryFn: () => service.fetchAgentsSummary(projectId!),
    enabled: !!projectId,
    refetchOnWindowFocus: false,
    staleTime: 60000,
  })

  const agents = useMemo<IssueAgentResponse[]>(() => {
    if (!summaryData) return []
    return summaryData.map((summary) => ({
      uuid: summary.uuid,
      name: summary.name,
      description: summary.description,
      agent_type: summary.agent_type,
      is_active: summary.is_active,
      is_default: summary.is_default,
      create_time: summary.create_time,
      config: summary.config ?? {},
      sensitivity: null,
      email_notifications: false,
      is_editor: summary.is_editor ?? false,
      is_enabled: summary.is_enabled ?? true,
      created_by: summary.created_by ?? null,
    }))
  }, [summaryData])

  const agentRunInfo = useMemo<Record<string, AgentRunInfo | null>>(() => {
    if (!summaryData) return {}
    const runInfoMap: Record<string, AgentRunInfo | null> = {}
    summaryData.forEach((summary) => {
      const latestRun = summary.latest_run
      if (latestRun) {
        const displayStatus = latestRun.status as AgentRunInfo['runStatus']
        const shouldShowTimestamp =
          displayStatus === 'completed' || displayStatus === 'failed'
        runInfoMap[summary.uuid] = {
          issueCount: latestRun.issue_count,
          lastRunTime: shouldShowTimestamp
            ? latestRun.completed_at || latestRun.create_time
            : null,
          runStatus: displayStatus,
        }
      } else {
        runInfoMap[summary.uuid] = null
      }
    })
    return runInfoMap
  }, [summaryData])

  return {
    agents,
    agentsPage,
    setAgentsPage,
    loadingAgents: isLoading,
    agentRunInfo,
    loadingRunInfo: false,
    loadAgents: async () => {
      await refetch()
    },
  }
}
